import { DialogContent } from 'Views/Page/Content/Modal/Components';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import TitleSection from 'Views/Page/Content/Modal/Components/TitleSection';
import React, { useCallback, useEffect } from 'react';
import {
  FDPrimaryActionButton, FDSecondaryActionButton, FDStack,
} from '@facet-wealth/design';
import { LottieLoadingLayout } from 'Organisms/LottieControl/LottieLoadingLayout';
import {
  FadeInBox,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedCommon/ExecuteSelectLoadingPage/FadeInBox';
import fr from 'facet-rest';
import {
  FacLoadingLottie,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedCommon/ExecuteSelectLoadingPage/FacLoadingLottie';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';

export const RetirementAgeGoalSeekPrioritiesStepLayout = () => {
  const { setModalCollection, clearModalCollection, modalResource: resource } = useModalContext();

  const { displayErrorMessage } = useSnackbarContext();

  const [saving, setSaving] = React.useState(false);

  // We set this to true at the tail end of getting the goal seek response and only then proceed to the next step
  const [proceed, setProceed] = React.useState(false);
  // submitResp holds the response for the next wizard step containing the results of the goal seek operation
  const [submitResp, setSubmitResp] = React.useState(undefined);

  // onSubmit submits this resource and waits for the goal seek operation to complete before setting it into submitResp
  const onSubmit = useCallback(async () => {
    try {
      setSaving(true);
      const resp = await resource.submit();
      setSubmitResp(resp);
      setProceed(true);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setSaving(false);
    }
  }, [resource]);

  // This useEffect watches changes to canceled and submitResp such that we only proceed to the next wizard step once
  // this step has successfully submitted && the user has not clicked cancel to close the modal during the wait.
  // Without this, a user that cancels will have the second wizard step pop up unexpectedly once the goal seek operation finishes.
  useEffect(() => {
    if (proceed === true) {
      setModalCollection(submitResp);
    }
  }, [proceed, submitResp]);

  return (
    <DialogContent>
      <FDStack direction={'column'} spacing={2} paddingX={4} paddingY={2}>
        <TitleSection>
          <MetaControlField attr={'title'} />
        </TitleSection>
        <FDStack direction={'column'} spacing={4} width={'100%'}>
          {!saving && <>
            <FDStack direction={'column'} spacing={4} width={'100%'}>
              <MetaControlField attr={'priorityHeader'} />
              <MetaControlField attr={'priority'} />
              <MetaControlField attr={'endBalanceHeader'} />
              <MetaControlField attr={'endBalance'} />
              <MetaControlField attr={'endBalanceDisabledHeader'} color={'textDisabled'} />
              {/* Setting value={0} here gets around some funkiness between the FD component and the field being readonly */}
              <MetaControlField attr={'endBalanceDisabled'} value={0} />
            </FDStack>
            <FDStack spacing={2} direction="row" alignItems={'center'} justifyContent={'center'} width={'100%'}>
              <FDPrimaryActionButton
                label={'Continue'}
                onClick={onSubmit}
                loading={saving}
                disabled={saving}
                size={'lg'}
              />
            </FDStack>
          </>}
          {saving && <>
            <FDStack direction={'column'} spacing={4} width={'100%'}>
              <MetaControlField attr={'loadingDescription'} />
              <LottieLoadingLayout>
                <FadeInBox>
                  {resource.withRelationship(fr.relationships.loadingLottie.name,
                    (rel) => (<FacLoadingLottie rel={rel} />),
                    () => null)}
                </FadeInBox>
              </LottieLoadingLayout>
              <FDStack spacing={2} direction="row" alignItems={'center'} justifyContent={'center'} width={'100%'}>
                <FDSecondaryActionButton
                  label={'Cancel'}
                  onClick={() => {
                    clearModalCollection();
                  }}
                  size={'md'}
                />
              </FDStack>
            </FDStack>
          </>}
        </FDStack>
      </FDStack>
    </DialogContent>
  );
};
